<!-- Container for signature positions and pending signatures -->
<!-- Exposes focusable divs for accessibility -->
<template>
  <div>
    <div
      v-for="(item, index) in signObjects"
      :key="index"
      @focus="selectAnnotation(item)"
      @blur="deselectAnnotation(item)"
      tabindex="0"
      :aria-label="`Page ${(item.coordinates.pageIndex || 0) + 1}, ${item.name} numéro ${index}`"
      :id="item.domId"
      class="outline-hidden"
    />
  </div>
</template>

<script setup lang="ts">
import { useAsyncOperation } from "@/app/composable/useAsyncOperation.ts";
import { useEnhanceSignatureApi } from "@/app/http/enhance-signature.api.ts";
import { useEnhanceSignatureStore } from "@/app/stores/enhance-signature.store.ts";
import { useLoadingStore } from "@/app/stores/loading.store.ts";
import { useSignaturePositionStore } from "@/app/stores/signature-position.store.ts";
import { FormOperation } from "@/app/types";
import { rotateImageBytes } from "@/app/utils/rotate-image.ts";
import { type Unsubscribe } from "@embedpdf/core";
import { PdfAnnotationSubtype } from "@embedpdf/models";
import { type AnnotationScope, useAnnotation } from "@embedpdf/plugin-annotation/vue";
import { useActiveDocument } from "@embedpdf/plugin-document-manager/vue";
import { useScroll } from "@embedpdf/plugin-scroll/vue";
import { useSelectionCapability } from "@embedpdf/plugin-selection/vue";
import { onKeyStroke } from "@vueuse/core";
import axios, { type AxiosResponse } from "axios";
import { debounce, noop, orderBy } from "lodash";
import { computed, type ComputedRef, onMounted, onUnmounted, type Ref, ref } from "vue";
import { useLetterFileStore } from "../../stores/letter-file.store.ts";
import { usePendingSignaturesStore } from "../../stores/pending-signatures.store.ts";
import { useRawSignaturePositionStore } from "../../stores/raw-signature-position.store.ts";
import { useSignEvents } from "./composable/use-sign-events.ts";
import { AbstractSignObject } from "./domain/abstract.sign-object.domain.ts";
import { getPageRotation } from "./domain/page-rotation.util.ts";
import { PendingSignature } from "./domain/pending-signature.domain.ts";
import { SignPosition } from "./domain/sign-position.domain.ts";

type Props = { idDocToSign: number };
const props = defineProps<Props>();

const letterFileStore = useLetterFileStore();
const letterFile = computed(() => letterFileStore.getCurrent());
const activeDocument = useActiveDocument();
const annotationApi = useAnnotation(activeDocument.activeDocumentId.value!).provides as Ref<AnnotationScope | null>;
const scrollProvide = useScroll(activeDocument.activeDocumentId.value!).provides;
const selectionProvide = useSelectionCapability().provides;
const rawSignPosStore = useRawSignaturePositionStore();
const signaturePositionStore = useSignaturePositionStore();
const pendingSignaturesStore = usePendingSignaturesStore();
const enhanceSignatureApi = useEnhanceSignatureApi();
const enhanceSignatureStore = useEnhanceSignatureStore();
const signEvents = useSignEvents();
const loadingStore = useLoadingStore();
const asyncOperation = useAsyncOperation();

const currentSelected: Ref<AbstractSignObject | null> = ref(null);
const signaturePositions: ComputedRef<SignPosition[]> = computed(() => signaturePositionStore.list as SignPosition[]);
const pendingSignatures: ComputedRef<PendingSignature[]> = computed(
  () => pendingSignaturesStore.list as PendingSignature[]
);
const signObjects = computed(() => {
  return orderBy([...signaturePositions.value, ...pendingSignatures.value], "page");
});

function selectAnnotation(signObj: AbstractSignObject) {
  currentSelected.value = signObj;
  signObj.select();
}

function deselectAnnotation(signObj: AbstractSignObject) {
  currentSelected.value = null;
  signObj.deselect();
}

async function remove(signObj: AbstractSignObject) {
  await asyncOperation.notifyAsyncOperation(async () => {
    await signObj.delete();
    if (signObj instanceof SignPosition) {
      signaturePositionStore.remove(signObj.idStr);
    } else if (signObj instanceof PendingSignature) {
      signaturePositions.value.find(item => item.isLinkedTo(signObj.idStr))?.unlink();
    }
  }, FormOperation.SIGN_POS);
}

async function fetchSignPos() {
  rawSignPosStore.setCurrent(props.idDocToSign, letterFile.value!.id);
  await rawSignPosStore.fetchCurrentDoc();

  signaturePositionStore.setList([]);
  for (const [id, signPos] of rawSignPosStore.map) {
    await signaturePositionStore.add(
      new SignPosition(
        () => annotationApi,
        () => scrollProvide,
        () => activeDocument,
        signPos,
        id
      )
    );
  }
}

const shouldFirstScroll = ref(false);

const scrollToFirstSignPosition = debounce(() => {
  const firstSignPosition = AbstractSignObject.sort(signaturePositionStore.list as SignPosition[])[0];
  firstSignPosition.scrollTo();

  shouldFirstScroll.value = false;
}, 500);

onKeyStroke(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"], e => {
  if (!currentSelected.value) {
    return;
  }

  if (e.altKey) {
    // resize
    const px = e.shiftKey ? 1 : 10;
    const direction = ["ArrowLeft", "ArrowDown"].includes(e.key) ? -1 : 1;
    if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
      currentSelected.value.resizeRelative({ width: px * direction });
    } else {
      currentSelected.value.resizeRelative({ height: px * direction });
    }
  } else {
    // move
    const px = e.shiftKey ? 1 : 10;
    const direction = ["ArrowLeft", "ArrowUp"].includes(e.key) ? -1 : 1;
    if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
      currentSelected.value.moveRelative({ x: px * direction });
    } else {
      currentSelected.value.moveRelative({ y: px * direction });
    }
  }

  e.preventDefault();
});

onKeyStroke("Backspace", async e => {
  if (currentSelected.value) {
    signEvents.emit("delete", currentSelected.value.idStr);
    e.preventDefault();
  }
});
onKeyStroke("Delete", async e => {
  if (currentSelected.value) {
    signEvents.emit("delete", currentSelected.value.idStr);
    e.preventDefault();
  }
});

const offArr: Unsubscribe[] = [];
onMounted(() => {
  pendingSignaturesStore.setList([]);
  const offLoadSignPos =
    annotationApi.value?.onAnnotationEvent(async event => {
      if (event.type === "loaded") {
        await fetchSignPos();
        shouldFirstScroll.value = rawSignPosStore.list.length > 0;
      } else if (event.type === "create" && event.committed && shouldFirstScroll.value) {
        scrollToFirstSignPosition();
      }
    }) || noop;
  offArr.push(offLoadSignPos);
});

onMounted(() => {
  const offUpdateCurrentSelected =
    annotationApi.value?.onStateChange(event => {
      const selectedUid = event.selectedUids[0];
      if ((currentSelected.value?.idStr || null) !== selectedUid) {
        currentSelected.value = signObjects.value.find(item => item.idStr === selectedUid) || null;
      }
    }) || noop;

  const offUpdateSignObject =
    annotationApi.value?.onAnnotationEvent(async event => {
      if (event.type === "update") {
        const updated = signObjects.value.find(signObject => signObject.idStr === event.annotation.id);
        updated?.update(event.patch.rect!);

        if (event.annotation.type === PdfAnnotationSubtype.STAMP) {
          signaturePositions.value.find(signPos => signPos.isLinkedTo(event.annotation.id))?.unlink();
        }
      }
    }) || noop;

  offArr.push(offUpdateCurrentSelected, offUpdateSignObject);
});

onMounted(() => {
  const offCreateSignPos = signEvents.on("createPos", async () => {
    await asyncOperation.notifyAsyncOperation(async () => {
      const signPos = new SignPosition(
        () => annotationApi,
        () => scrollProvide,
        () => activeDocument
      );
      await signaturePositionStore.add(signPos);

      signPos.select({ scrollTo: false });
    }, FormOperation.SIGN_POS);
  });

  offArr.push(offCreateSignPos);
});

onMounted(() => {
  const offPlacePendingSign = signEvents.on("placePending", async signObjectId => {
    loadingStore.setLoading("sign-placement", true);
    const enhanceOptions = enhanceSignatureStore.getOptions(signObjectId);
    const {
      url,
      originalWidth: width,
      originalHeight: height,
    } = await enhanceSignatureApi.generateEnhancedSignature({ signatureId: signObjectId, ...enhanceOptions });
    const imageResponse: AxiosResponse<ArrayBuffer> = await axios.get(url, {
      responseType: "arraybuffer",
      headers: { Accept: "image/*" },
    });

    const prepositions = signaturePositions.value.filter(signPosition => !signPosition.isLinked);
    const newSignatures: PendingSignature[] = [];
    let scrollToSelect = true;
    let toSelect;
    if (!prepositions.length) {
      const pendingSignature = new PendingSignature(
        signObjectId,
        enhanceOptions,
        { width, height },
        () => annotationApi,
        () => scrollProvide,
        () => activeDocument
      );
      newSignatures.push(pendingSignature);
      toSelect = pendingSignature;
      scrollToSelect = false;
    } else {
      for (const preposition of prepositions) {
        const pendingSignature = new PendingSignature(
          signObjectId,
          enhanceOptions,
          { width, height },
          () => annotationApi,
          () => scrollProvide,
          () => activeDocument,
          preposition.coordinates
        );
        newSignatures.push(pendingSignature);
        toSelect = toSelect ?? pendingSignature;
        preposition.linkTo(pendingSignature.idStr);
      }
    }

    // https://www.embedpdf.com/docs/vue/headless/plugins/plugin-annotation#importing-stamps-from-image-files
    // annotation with images must be imported all at the same time
    //
    // The image is embedded into the page's raw content space, same as
    // everything else, but unlike a plain shape it has an inherent "up" -
    // counter-rotate it so it reads upright once PdfViewer's CSS rotation
    // is applied to it too.
    const imageStamps = await Promise.all(
      newSignatures.map(async pendingSignature => {
        const annotation = pendingSignature.buildAnnotation();
        const rotation = getPageRotation(pendingSignature.coordinates.pageIndex, activeDocument.activeDocument.value);
        const data = await rotateImageBytes(imageResponse.data, rotation);
        return { annotation, ctx: { data } };
      })
    );
    annotationApi.value!.importAnnotations(imageStamps);
    toSelect!.select({ scrollTo: scrollToSelect });

    loadingStore.setLoading("sign-placement", false);
  });

  offArr.push(offPlacePendingSign);
});

onMounted(() => {
  const offDeleteSignObject = signEvents.on("delete", async id => {
    const found = signObjects.value.find(sp => sp.idStr === id);
    if (found) {
      await remove(found);
    }
  });

  offArr.push(offDeleteSignObject);
});

onMounted(() => {
  const offDeleteObjects = signEvents.on("deleteObjects", async (type, pageIndex) => {
    const collection = type === "signPos" ? signaturePositions.value : pendingSignatures.value;
    const toDelete =
      pageIndex === undefined ? collection : collection.filter(object => object.coordinates.pageIndex === pageIndex);
    toDelete.forEach(remove);
  });

  offArr.push(offDeleteObjects);
});

// https://gitlab-forge.din.developpement-durable.gouv.fr/snum/pnm3/gti/parapheur/parapheur-home/-/work_items/1316
onMounted(() => {
  const offDisableSelection =
    selectionProvide.value?.onEndSelection(() => {
      selectionProvide.value!.clear();
    }) || noop;

  offArr.push(offDisableSelection);
});

onUnmounted(() => {
  for (const off of offArr) {
    off();
  }
});
</script>
